/**
 * Engine-aware object-type checkbox list for the Database Blueprint setup dialog.
 *
 * Rows that are irrelevant for the chosen engine are hidden entirely so the
 * dialog only ever offers actions the user can actually take.
 */

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type {
  BlueprintObjectTypes,
  UserFacingEngineId,
} from '../types/databaseBlueprintTypes';

type Props = {
  engine: UserFacingEngineId;
  value: BlueprintObjectTypes;
  onChange: (next: BlueprintObjectTypes) => void;
};

type Row = {
  key: keyof BlueprintObjectTypes;
  label: string;
  /** Optional hover tooltip — kept out of the visible label (Finding #22e). */
  tooltip?: string;
  /** Engines on which this object type is supported. */
  engines: ReadonlyArray<UserFacingEngineId>;
};

// Finding #22e — drop "(inline with tables)" parenthetical from the visible
// row label; the engine-emit detail belongs in a hover tooltip, not the
// dialog's main copy.
const ROWS: ReadonlyArray<Row> = [
  { key: 'tables', label: 'Tables', engines: ['mssql', 'pgsql', 'mysql', 'oracle', 'sqlite'] },
  { key: 'views', label: 'Views', engines: ['mssql', 'pgsql', 'mysql', 'oracle', 'sqlite'] },
  { key: 'procedures', label: 'Stored procedures', engines: ['mssql', 'pgsql', 'mysql', 'oracle'] },
  { key: 'functions', label: 'Functions', engines: ['mssql', 'pgsql', 'mysql', 'oracle'] },
  {
    key: 'triggers',
    label: 'Triggers',
    tooltip: 'Emitted inline with their parent tables.',
    engines: ['mssql', 'pgsql', 'mysql', 'oracle', 'sqlite'],
  },
  {
    key: 'userTypes',
    label: 'User-defined types / table types',
    tooltip:
      'SQL Server table types, alias types and CLR types, emitted to Programmability/User-Defined Types.',
    // SQL Server only. The row shipped claiming `pgsql` and `oracle` too while
    // NOTHING emitted user types on any engine; PostgreSQL domains / enums /
    // composites and Oracle object types are still not modelled (neither the
    // emitter nor Schema Compare reads them), so the row would go on promising
    // a folder that never appears. `USER_TYPE_ENGINES` is the same fact on the
    // emit side.
    engines: ['mssql'],
  },
  { key: 'sequences', label: 'Sequences', engines: ['pgsql', 'oracle', 'mssql'] },
  { key: 'synonyms', label: 'Synonyms', engines: ['mssql', 'oracle'] },
  { key: 'permissions', label: 'Permissions / grants', engines: ['mssql', 'pgsql', 'mysql', 'oracle'] },
  { key: 'schemas', label: 'Schema CREATE statements', engines: ['mssql', 'pgsql', 'oracle'] },
  { key: 'packages', label: 'Packages', engines: ['oracle'] },
];

export function ObjectTypeCheckboxList({ engine, value, onChange }: Props) {
  const toggle = (key: keyof BlueprintObjectTypes) => {
    onChange({ ...value, [key]: !value[key] });
  };
  const visibleRows = ROWS.filter((row) => row.engines.includes(engine));
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="database-blueprint-object-types">
      {visibleRows.map((row) => {
        const id = `blueprint-obj-${row.key}`;
        return (
          <div key={row.key} className="flex items-center gap-2">
            <Checkbox
              id={id}
              checked={value[row.key]}
              onCheckedChange={() => toggle(row.key)}
              data-testid={`blueprint-obj-${row.key}`}
            />
            <Label htmlFor={id} className="text-sm" title={row.tooltip}>
              {row.label}
            </Label>
          </div>
        );
      })}
    </div>
  );
}
